import { ProofClient, ProofApiError, damFormServices } from "@proof/sdk";
import { defaultFontLoader } from "@proof/fonts/browser";
import { checkedPreviewBlob } from "@proof/ui";
import type { AuthoringServices } from "@proof/authoring";
import type {
  Principal,
  Project,
  Values,
  OutputProfile,
  Field,
  CapabilityId,
  TestSelection,
} from "@proof/core";
import { downloadBlob } from "./api";
import type { JobView } from "./api";
import { openNativePreview, type NativePreviewSeed } from "./native-preview";

export function createWorkspaceTransport(options: {
  demo: boolean;
  principal?: Principal;
  token?: string;
  previewSubject?: string | null;
  onAccessLost?: () => void;
}) {
  const profiles = new Map<string, { name: string; values: Values }[]>();
  const previews = new Set<ReturnType<typeof openNativePreview>>();
  const lifetime = new AbortController(),
    headers: Readonly<Record<string, string>> = Object.freeze<
      Record<string, string>
    >({
      ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      ...(options.previewSubject !== undefined
        ? {
            "X-Proof-Preview-User":
              options.previewSubject === null
                ? "none"
                : `user:${encodeURIComponent(options.previewSubject)}`,
          }
        : {}),
    }),
    active = () => {
      if (lifetime.signal.aborted)
        throw new DOMException("Workspace closed", "AbortError");
    },
    signal = (external?: AbortSignal | null) =>
      external ? AbortSignal.any([external, lifetime.signal]) : lifetime.signal;
  const request = async (path: string, init: RequestInit = {}) => {
    active();
    if (
      !path.startsWith("/") ||
      path.startsWith("//") ||
      /[\\\r\n\u0000]/.test(path)
    )
      throw new Error("Invalid API path");
    const scopedHeaders = new Headers(init.headers);
    for (const [name, value] of Object.entries(headers))
      scopedHeaders.set(name, value);
    const response = await fetch(`/api${path}`, {
      ...init,
      redirect: "error",
      credentials: options.token ? "omit" : "same-origin",
      signal: signal(init.signal),
      headers: scopedHeaders,
    });
    if (lifetime.signal.aborted) {
      await response.body?.cancel();
      active();
    }
    if (!response.ok) {
      const problem = await response.json().catch(() => ({}));
      active();
      if (
        response.status === 401 ||
        ["TENANT_MEMBERSHIP_REVOKED", "OPERATOR_TENANT_FORBIDDEN"].includes(
          problem.code || problem.error,
        )
      )
        options.onAccessLost?.();
      throw new ProofApiError(
        typeof problem.error === "string" ? problem.error : "Request failed",
        response.status,
        problem.code,
      );
    }
    return response;
  };
  class ScopedClient extends ProofClient {
    override async loadGenerationArtifact(id: string, abort?: AbortSignal) {
      const response = await request(
        `/jobs/${encodeURIComponent(id)}/artifact`,
        { signal: abort },
      );
      return new Blob([await bytes(response, 200000000)], {
        type: response.headers.get("content-type")?.split(";")[0] || "",
      });
    }
    override async request<T>(
      path: string,
      init: RequestInit = {},
      responseType: "json" | "blob" = "json",
    ): Promise<T> {
      const contentHeaders = new Headers(init.headers);
      if (!contentHeaders.has("Content-Type"))
        contentHeaders.set("Content-Type", "application/json");
      const response = await request(path, {
        ...init,
        headers: contentHeaders,
      });
      const result =
        response.status === 204
          ? undefined
          : responseType === "blob"
            ? await response.blob()
            : await response.json();
      active();
      return result as T;
    }
  }
  const client = new ScopedClient("", { ...headers });
  const bytes = async (response: Response, limit: number) => {
    if (
      Number(response.headers.get("Content-Length")) > limit ||
      !response.body
    ) {
      await response.body?.cancel();
      throw new Error("Resource exceeds its size limit");
    }
    const reader = response.body.getReader(),
      chunks: Uint8Array[] = [];
    let count = 0;
    try {
      for (;;) {
        const chunk = await reader.read();
        if (chunk.done) break;
        count += chunk.value.byteLength;
        if (count > limit) throw new Error("Resource exceeds its size limit");
        chunks.push(new Uint8Array(chunk.value));
      }
      active();
      const result = new Uint8Array(count);
      let offset = 0;
      for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.length;
      }
      return result;
    } finally {
      await reader.cancel().catch(() => {});
      reader.releaseLock();
    }
  };
  const image = async (response: Response) =>
    checkedPreviewBlob(
      new Blob([await bytes(response, 20000000)], {
        type: response.headers.get("Content-Type")?.split(";")[0] || "",
      }),
    );
  const loadAsset: NonNullable<AuthoringServices["loadAsset"]> = async (
    id,
    abort,
  ) =>
    image(
      await request(`/assets/${encodeURIComponent(id)}`, { signal: abort }),
    );
  const transformImage: NonNullable<
    AuthoringServices["transformImage"]
  > = async (id, adjustments, abort) =>
    image(
      await request(`/assets/${encodeURIComponent(id)}/transform`, {
        method: "POST",
        signal: abort,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adjustments }),
      }),
    );
  const loadFont: NonNullable<AuthoringServices["loadFont"]> = options.demo
    ? defaultFontLoader
    : async (face, abort) =>
        bytes(
          await request(`/fonts/${encodeURIComponent(face.id)}/file`, {
            signal: abort,
          }),
          8388608,
        );
  const upload = (
    file: File,
    _field?: Field,
    abort?: AbortSignal,
    projectId?: string,
  ) => client.uploadAsset(file, file.name, abort, projectId);
  // Live proof / test-window selections that are not one published version (drafts, mirrored forms).
  const testSelections = new Map<string, TestSelection>();
  const setTestSelection = (
    projectId: string,
    selection: TestSelection | undefined,
  ) => {
    if (selection) testSelections.set(projectId, selection);
    else testSelections.delete(projectId);
  };
  const projectInput = (project: Project, versionId?: string) => {
    if (options.demo) return { project };
    const testSelection = versionId
      ? undefined
      : testSelections.get(project.id);
    return {
      projectId: project.id,
      ...(versionId ? { versionId } : {}),
      ...(testSelection ? { testSelection } : {}),
    };
  };
  const authoring: AuthoringServices = {
    listFieldPresets: (query) => client.listFieldPresets(query),
    ...(options.demo ||
    options.principal?.authorization?.capabilities.includes(
      "tenant.resources.manage",
    )
      ? {
          saveFieldPreset: (input: import("@proof/core").FieldPresetWrite) =>
            client.saveFieldPreset(input),
        }
      : {}),
    listResources: () => client.listResources(),
    profileDataset: (resource) => client.profileDataset(resource),
    ...(options.demo ||
    options.principal?.authorization?.capabilities.includes(
      "tenant.resources.manage",
    )
      ? {
          saveProfileDataset: (
            input: import("@proof/core").ProfileDatasetWrite,
            resource: import("@proof/core").Resource,
          ) => client.saveProfileDataset(input, resource),
        }
      : {}),
    saveResource: (resource) => client.saveResource(resource),
    importBase: async (file, abort) =>
      (await client.importDocument(file, file.name, abort)).project,
    importPdfArtwork: (file, page, signal) =>
      client.importPdfArtwork(file, file.name, page, signal),
    listAssets: (abort) => client.listAssets(abort),
    listDamProviders: () => client.listDamProviders(),
    dam: (project, partId) => damFormServices(client, project, partId),
    listFonts: async () =>
      (await client.listFonts())
        .filter((row) => row.status === "active")
        .map((row) => row.face),
    loadFont,
    upload,
    ...(options.demo
      ? { assetUrl: (id: string) => `/api/assets/${encodeURIComponent(id)}` }
      : { loadAsset, transformImage }),
  };
  return {
    demo: options.demo,
    principal: options.principal,
    client,
    headers,
    request,
    api: client.request.bind(client),
    upload,
    loadFont,
    authoring,
    loadAsset: options.demo ? undefined : loadAsset,
    proofServices: options.demo
      ? {
          loadFont,
          // Demo assets (uploads, URL imports) are served by the API.
          assetUrl: (id: string) => `/api/assets/${encodeURIComponent(id)}`,
        }
      : { loadFont, loadAsset, transformImage },
    projectInput,
    setTestSelection,
    openPreview: (
      seed: NativePreviewSeed,
      windowOptions?: { newWindow?: boolean },
    ) => {
      active();
      if (options.demo || !options.principal)
        throw new Error(
          "Connect a tenant workspace before opening a native preview.",
        );
      if (previews.size >= 8)
        throw new Error(
          "Close an existing proof preview before opening another.",
        );
      const handoff = openNativePreview({
        newWindow: windowOptions?.newWindow,
        principal: options.principal,
        token: options.token,
        seed,
        signal: lifetime.signal,
        onClose: () => previews.delete(handoff),
      });
      previews.add(handoff);
    },
    profiles: {
      load: (key: string) => {
        active();
        return structuredClone(profiles.get(key) || []);
      },
      save: (key: string, rows: { name: string; values: Values }[]) => {
        active();
        if (
          rows.length > 100 ||
          rows.some((row) => !row.name.trim() || row.name.length > 200) ||
          new TextEncoder().encode(JSON.stringify(rows)).length > 1000000 ||
          (!profiles.has(key) && profiles.size >= 100)
        )
          throw new Error("Session form profiles exceed the supported size");
        profiles.set(key, structuredClone(rows));
      },
    },
    can: (capability: CapabilityId) =>
      options.demo ||
      !!options.principal?.authorization?.capabilities.includes(capability),
    generate: (
      project: Project,
      values: Values,
      profile: OutputProfile,
      partId?: string,
      context: {
        demoProfile?: string;
        contextHash?: string;
        sourceParameters?: import("@proof/core").SourceParameters;
        duplicatePolicy?: "confirm";
        replaceJobId?: string;
      } = {},
      versionId?: string,
    ) => {
      if (!options.demo && !versionId)
        throw new Error("Select a published version before generating");
      return client.request<JobView>("/jobs", {
        method: "POST",
        body: JSON.stringify({
          ...projectInput(project, versionId),
          values,
          partId,
          duplicatePolicy: context.duplicatePolicy,
          replaceJobId: context.replaceJobId,
          ...(options.demo
            ? { profile, ...context }
            : {
                profileId: profile.id,
                contextHash: context.contextHash,
                sourceParameters: context.sourceParameters,
              }),
        }),
      });
    },
    download: async (path: string, filename: string, view = false) => {
      const opened = view ? window.open("about:blank", "_blank") : null;
      if (opened) opened.opener = null;
      try {
        const blob = await (await request(path)).blob();
        active();
        if (view && opened) {
          const url = URL.createObjectURL(blob);
          opened.location.href = url;
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        } else downloadBlob(blob, filename);
      } catch (error) {
        opened?.close();
        throw error;
      }
    },
    close: () => {
      profiles.clear();
      lifetime.abort();
      for (const preview of previews) preview.close();
      previews.clear();
    },
  };
}
export type WorkspaceTransport = ReturnType<typeof createWorkspaceTransport>;
